import {
  forwardRef,
  KeyboardEventHandler,
  MouseEventHandler,
  SetStateAction,
  useState,
} from 'react';

import { SelectMultiple } from './SelectMultiple';
import { SelectSingle } from './SelectSingle';
import { SelectProps } from './types';

export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  ({ options, onChange, multiple, value, ...rest }, ref) => {
    const [open, setOpen] = useState(false);
    const [activeIndex, setActiveIndex] = useState<null | number>(null);

    // Toggle list
    const handleToggleList = () => {
      setOpen(!open);
      setActiveIndex(null);
    };

    // Main reset selection
    const handleResetSelection = () => {
      onChange?.(null);
    };

    // Reset Icon using click
    const handleClickReset: MouseEventHandler = (e) => {
      e.stopPropagation();
      handleResetSelection();
    };

    // Reset Icon using KeyBoard
    const handleKeyDownReset: KeyboardEventHandler = (e) => {
      if (![' ', 'Enter'].includes(e.key)) return;
      e.stopPropagation();
      handleResetSelection();
    };

    const handlerSetActiveIndex = (action: SetStateAction<number | null>) => {
      setActiveIndex(action);
    };

    const keyBoardHandlers = (
      commitSelection: (idx: number | null) => void,
      listLength = options.length,
    ): KeyboardEventHandler => {
      const hasOptions = listLength > 0;
      const lastIndex = hasOptions ? listLength - 1 : 0;

      return (e) => {
        switch (e.key) {
          case ' ':
            e.preventDefault();
            handleToggleList();
            break;
          case 'Enter':
            e.preventDefault();
            commitSelection(activeIndex);
            break;
          case 'ArrowDown':
            e.preventDefault();
            if (!open) {
              handleToggleList();
            } else if (hasOptions) {
              handlerSetActiveIndex((i) => Math.min(lastIndex, (i ?? -1) + 1));
            }
            break;
          case 'ArrowUp':
            e.preventDefault();
            if (!open) {
              handleToggleList();
            } else if (hasOptions) {
              handlerSetActiveIndex((i) => Math.max(0, (i ?? lastIndex) - 1));
            }
            break;
          case 'Home':
            if (open && hasOptions) {
              e.preventDefault();
              handlerSetActiveIndex(0);
            }
            break;
          case 'End':
            if (open && hasOptions) {
              e.preventDefault();
              handlerSetActiveIndex(lastIndex);
            }
            break;
          case 'Escape':
            if (open) {
              e.preventDefault();
              handleToggleList();
            }
            break;
          default:
            return;
        }
      };
    };

    if (multiple) {
      return (
        <SelectMultiple
          ref={ref}
          open={open}
          activeIndex={activeIndex}
          keyBoardHandlers={keyBoardHandlers}
          onToggleList={handleToggleList}
          onClickReset={handleClickReset}
          onKeyDownReset={handleKeyDownReset}
          onChange={onChange}
          options={options}
          value={value}
          {...rest}
        />
      );
    }

    return (
      <SelectSingle
        ref={ref}
        open={open}
        activeIndex={activeIndex}
        keyBoardHandlers={keyBoardHandlers}
        onToggleList={handleToggleList}
        onClickReset={handleClickReset}
        onKeyDownReset={handleKeyDownReset}
        onChange={onChange}
        options={options}
        value={value}
        {...rest}
      />
    );
  },
);

Select.displayName = 'Select';
